// context/CartContext.jsx
import React, { createContext, useContext, useState } from 'react';
import axios from 'axios';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export function CartProvider({ children }) {
  const [cart, setCart] = useState([]);
  const [cartMessage, setCartMessage] = useState({ type: '', text: '' });
  const [exchangeRate, setExchangeRate] = useState(null);

  // Add function to fetch exchange rate
  const fetchExchangeRate = async (jwtToken) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/v1/share_catalog/exchange_rate`,
        {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${jwtToken}`
          }
        }
      );
      setExchangeRate(response.data[0].rate);
    } catch (error) {
      console.error('Error fetching exchange rate:', error);
    }
  };

  // Add function to calculate USD price
  const convertToUSD = (price, currency) => {
    if (currency === 'USD') return price;
    if (currency === 'KHR' && exchangeRate) {
      return price / exchangeRate;
    }
    console.log(`Conversion from ${currency} to USD not supported`);
    return price; // fallback to original price if no conversion possible
  };

  const addToCart = (item, currentProducts, updateProducts) => {
    setCart(prev => {
      const existingItem = prev.find(i =>
        i.id === item.id &&
        i.size === item.size &&
        i.color === item.color
      );

      const newCart = existingItem
        ? prev.map(i =>
          i.id === item.id && i.size === item.size && i.color === item.color
            ? { ...i, quantity: i.quantity + item.quantity }
            : i
        )
        : [...prev, item];

      updateInventory(newCart, currentProducts, updateProducts);

      setCartMessage({
        type: 'success',
        text: existingItem ? 'Cart updated successfully' : 'Item added to cart'
      });
      setTimeout(() => setCartMessage({ type: '', text: '' }), 3000);

      return newCart;
    });
  };

  const removeFromCart = (item, currentProducts, updateProducts) => {
    setCart(prev => {
      const newCart = prev.filter(i =>
        !(i.id === item.id && i.size === item.size && i.color === item.color)
      );

      updateInventory(newCart, currentProducts, updateProducts);

      setCartMessage({
        type: 'success',
        text: 'Item removed from cart'
      });
      setTimeout(() => setCartMessage({ type: '', text: '' }), 3000);

      return newCart;
    });
  };

  const updateInventory = (currentCart, originalProducts, updateProducts) => {
    let updatedProducts = JSON.parse(JSON.stringify(originalProducts));

    currentCart.forEach(cartItem => {
      updatedProducts = updatedProducts.map(product => {
        if (product.fashion_id === cartItem.id) {
          const updatedData = product.data.map(variation => {
            if (variation.color_name === cartItem.color &&
              variation.size_name === cartItem.size) {
              return {
                ...variation,
                quantity: variation.quantity - cartItem.quantity
              };
            }
            return variation;
          });
          return { ...product, data: updatedData };
        }
        return product;
      });
    });

    updateProducts(updatedProducts);
  };

  return (
    <CartContext.Provider value={{
      cart,
      setCart,
      addToCart,
      removeFromCart,
      cartMessage,
      fetchExchangeRate,
      convertToUSD,
      exchangeRate
    }}>
      {children}
    </CartContext.Provider>
  );
}